import { Decimal } from 'decimal.js';
import { toast } from 'sonner';
import { ItemSalePriceRule, PricingMethodLiteral, TaxRateWithComponents } from '@/app/types/itemSalePriceRule';
import { getActiveRulesForItem } from '@/app/services/itemSalePriceRuleService';

// Return type for calculateSalePriceWithRules function
export interface SalePriceCalculationResult {
  salePrice: Decimal | null; // Null if no rule applied and no base purchase price to calculate from
  appliedRuleId: number | null;
  source: 'rule' | 'item_price' | 'purchase_price' | 'not_found'; // To indicate where the price came from
}

// Define a proper interface for the getFinalItemPriceDetails return type
export interface FinalItemPriceDetails {
  rate: Decimal;
  discount_percent: Decimal;
  discount_amount: Decimal;
  tax_rate_id: number | null;
  tax_amount: Decimal;
  tax_breakdown: Array<{ code: string; rate: Decimal; amount: Decimal }>;
  final_amount: Decimal;
  taxable_amount: Decimal;
  applied_sale_rule_id: number | null;
  source: 'rule' | 'item_price' | 'purchase_price' | 'not_found';
  is_tax_inclusive?: boolean;
}

// Define an interface for the SQLite response type to match the window.api.db.getItemPriceDetails return type
interface SQLitePriceDetails {
  rate?: string | number;
  discount_percent?: string | number;
  discount_amount?: string | number;
  tax_rate_id?: number | null;
  tax_amount?: string | number;
  tax_breakdown?: Array<{ code?: string, rate: string | number, amount: string | number }>;
  final_amount?: string | number;
  taxable_amount?: string | number;
  applied_sale_rule_id?: number | null;
  is_tax_inclusive?: boolean;
}

/**
 * Helper function to safely parse values into Decimal
 */
function tryDecimalParse(value: string | number | Decimal | null | undefined): Decimal {
  try {
    // Handle undefined, null, or empty values
    if (value === undefined || value === null || value === '') {
      return new Decimal(0);
    }
    
    // If value is already a Decimal, return it directly
    if (value instanceof Decimal) {
      return value;
    }
    
    // Convert to string first to ensure Decimal can parse it
    return new Decimal(String(value));
  } catch (error) {
    console.warn(`[salePriceRuleUtils] Error parsing value "${value}" to Decimal:`, error);
    return new Decimal(0);
  }
}

/**
 * Helper function to round a rate based on configured decimal places
 * @param rate - The rate to round
 * @param decimalPlaces - Number of decimal places (0-4), defaults to 0
 * @returns Rounded rate as Decimal
 */
export function roundRate(rate: Decimal | number | string, decimalPlaces: number = 0): Decimal {
  const rateDecimal = tryDecimalParse(rate);
  
  // Ensure decimal places is within valid range
  const validDecimalPlaces = Math.max(0, Math.min(4, decimalPlaces || 0));
  
  if (validDecimalPlaces === 0) {
    return rateDecimal.round();
  }
  
  return rateDecimal.toDecimalPlaces(validDecimalPlaces, Decimal.ROUND_HALF_UP);
}

/**
 * Helper function to format a rate for display based on decimal places
 * @param rate - The rate to format
 * @param decimalPlaces - Number of decimal places (0-4), defaults to 0
 * @returns Formatted rate as string
 */
export function formatRateForDisplay(rate: Decimal | number | string, decimalPlaces: number = 0): string {
  const roundedRate = roundRate(rate, decimalPlaces);
  const validDecimalPlaces = Math.max(0, Math.min(4, decimalPlaces || 0));
  
  // For 0 decimal places, don't show decimal point
  if (validDecimalPlaces === 0) {
    return roundedRate.toFixed(0);
  }
  
  // For other cases, show exact decimal places
  return roundedRate.toFixed(validDecimalPlaces);
}

/**
 * Helper function to validate if a rate input is valid for given decimal places
 * @param input - The input string to validate
 * @param decimalPlaces - Number of decimal places allowed
 * @returns true if valid, false otherwise
 */
export function isValidRateInput(input: string, decimalPlaces: number = 0): boolean {
  // Allow empty string
  if (input === '') return true;
  
  // Allow negative sign at start
  if (input === '-') return true;
  
  // Check if it's a valid number format
  const regex = decimalPlaces === 0 
    ? /^-?\d*$/ // Only integers
    : new RegExp(`^-?\\d*\\.?\\d{0,${decimalPlaces}}$`); // Allow up to N decimal places
    
  return regex.test(input);
}

/**
 * Helper function to get purchase price for an item from local SQLite
 */
async function getItemPurchasePrice(itemId: number, siteId?: number | null): Promise<Decimal | null> {
  try {
    console.log(`[salePriceRuleUtils] Fetching purchase price for item ${itemId}, siteId: ${siteId || 'none'}`);
    
    // Import the async database function
    const { getItemPriceAsync } = await import('@/lib/indexeddb/database');
    
    // Get purchase price from local SQLite
    const price = await getItemPriceAsync(itemId, 'purchase', siteId);
    
    if (price !== null) {
      console.log(`[salePriceRuleUtils] Found purchase price in local SQLite: ${price}`);
      return tryDecimalParse(price);
    }
    
    console.warn(`[salePriceRuleUtils] No purchase price found for item ${itemId} in local SQLite`);
    return null;
  } catch (error) {
    console.error(`[salePriceRuleUtils] Error fetching purchase price from local SQLite for item ${itemId}:`, error);
    return null;
  }
}

/**
 * Calculates sale price based on pricing rules
 * 
 * This function fetches applicable sales item price rules and calculates the sale price
 * based on the highest priority rule and the provided purchase price.
 * 
 * @param itemId - The ID of the item
 * @param manualPurchasePrice - Optional manual purchase price
 * @param siteId - Optional site ID for site-specific pricing
 * @param quantity - Optional quantity for quantity-based rules (reserved for future use)
 * @returns Sale price calculation result
 */
export async function calculateSalePriceWithRules(
  itemId: number,
  manualPurchasePrice?: Decimal | string | number | null,
  siteId?: number | null,
  quantity: number | string = 1,
  rateDecimalPlaces: number = 0
): Promise<SalePriceCalculationResult> {
  console.log(`[salePriceRuleUtils] ========== CALCULATING SALE PRICE ==========`);
  console.log(`[salePriceRuleUtils] Item ID: ${itemId}, Site ID: ${siteId || 'none'}, Quantity: ${quantity}`);
  
  // Convert or fetch purchase price
  let purchasePrice: Decimal | null = null;
  
  // Convert quantity to Decimal for future quantity-based rules
  const quantityDecimal = tryDecimalParse(quantity);
  console.log(`[salePriceRuleUtils] Calculate price for item ${itemId}, quantity: ${quantityDecimal.toString()}`);

  if (manualPurchasePrice !== undefined && manualPurchasePrice !== null) {
    purchasePrice = tryDecimalParse(manualPurchasePrice);
    console.log(`[salePriceRuleUtils] Using manual purchase price: ${purchasePrice.toString()}`);
  } else {
    // If no manual purchase price, try to fetch it
    console.log(`[salePriceRuleUtils] No manual purchase price provided, fetching from API...`);
    purchasePrice = await getItemPurchasePrice(itemId, siteId);
    if (purchasePrice) {
      console.log(`[salePriceRuleUtils] Found purchase price from API: ${purchasePrice.toString()}`);
    } else {
      console.log(`[salePriceRuleUtils] No purchase price found for item ${itemId} - profit-based rules may not be applied.`);
    }
  }

  try {
    // 1. Fetch applicable sales item price rules - pass only itemId as expected by the function
    const activeRules = await getActiveRulesForItem(itemId);
    console.log(`[salePriceRuleUtils] Found ${activeRules?.length || 0} active rules for item ${itemId}`);
    console.log('[salePriceRuleUtils] Active rules:', JSON.stringify(activeRules, null, 2));
    
    if (!activeRules || activeRules.length === 0) {
      console.log(`[salePriceRuleUtils] No active rules found for item ${itemId}`);
      return { salePrice: null, appliedRuleId: null, source: 'item_price' };
    }

    // Filter rules by date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today's date
    
    const validRules = activeRules.filter(rule => {
      const effectiveFrom = rule.effective_from ? new Date(rule.effective_from) : null;
      const effectiveTo = rule.effective_to ? new Date(rule.effective_to) : null;

      if (effectiveFrom && effectiveFrom > today) return false;
      if (effectiveTo && effectiveTo < today) return false;
      return true;
    });

    console.log(`[salePriceRuleUtils] Found ${validRules.length} valid rules (within date range) for item ${itemId}`);
    console.log('[salePriceRuleUtils] Valid rules:', JSON.stringify(validRules, null, 2));

    if (validRules.length === 0) {
      return { salePrice: null, appliedRuleId: null, source: 'item_price' };
    }

    // First look for fixed sale price rules (these don't need purchase price)
    const fixedSalePriceRules = validRules.filter(rule => 
      rule.pricing_method === PricingMethodLiteral.FIXED_SALE_PRICE || 
      rule.pricing_method === PricingMethodLiteral.FIXED
    );
    
    console.log(`[salePriceRuleUtils] Found ${fixedSalePriceRules.length} fixed sale price rules`);
    
    // If there are fixed price rules, prioritize them
    if (fixedSalePriceRules.length > 0) {
      // 2. Select the best rule based on priority - first site-specific, then general
      let bestRule: ItemSalePriceRule | null = null;

      if (siteId) {
        bestRule = fixedSalePriceRules.find(rule => rule.site_id === siteId) || null;
        if (bestRule) {
          console.log(`[salePriceRuleUtils] Selected site-specific fixed sale price rule ID ${bestRule.id}`);
        }
      }

      // If no site-specific rule found, look for a general rule
      if (!bestRule) {
        bestRule = fixedSalePriceRules.find(rule => rule.site_id === null) || null;
        if (bestRule) {
          console.log(`[salePriceRuleUtils] Selected general fixed sale price rule ID ${bestRule.id}`);
        }
      }

      // If we found a fixed sale price rule, use it
      if (bestRule) {
        const ruleValue = tryDecimalParse(bestRule.value);
        console.log(`[salePriceRuleUtils] Applied FIXED_SALE_PRICE rule with value ${ruleValue.toString()}`);
        
        // Round based on item's decimal places configuration
        const roundedRuleValue = roundRate(ruleValue, rateDecimalPlaces);
        
        return {
          salePrice: roundedRuleValue,
          appliedRuleId: bestRule.id,
          source: 'rule',
        };
      }
    }
    
    // If we don't have purchase price, and no fixed sale price rules,
    // we can't apply profit-based rules
    if (!purchasePrice || purchasePrice.isZero()) {
      console.log(`[salePriceRuleUtils] No purchase price available and no fixed sale price rules - falling back to standard pricing`);
      return { salePrice: null, appliedRuleId: null, source: 'item_price' };
    }
    
    // Now try profit-based rules since we have a purchase price
    const profitRules = validRules.filter(rule => 
      rule.pricing_method === PricingMethodLiteral.PROFIT_PERCENTAGE || 
      rule.pricing_method === PricingMethodLiteral.FIXED_PROFIT
    );
    
    console.log(`[salePriceRuleUtils] Found ${profitRules.length} profit-based rules`);
    console.log('[salePriceRuleUtils] Profit rules:', JSON.stringify(profitRules, null, 2));
    
    if (profitRules.length > 0) {
      // Select rules based on quantity
      console.log(`[salePriceRuleUtils] Filtering rules by quantity: ${quantityDecimal.toString()}`);
      
      const quantityMatchedRules = profitRules.filter(rule => {
        const minQty = tryDecimalParse(rule.min_qty);
        const maxQty = rule.max_qty ? tryDecimalParse(rule.max_qty) : null;
        
        console.log(`[salePriceRuleUtils] Checking rule ID ${rule.id} - minQty: ${minQty}, maxQty: ${maxQty || 'none'}, qty: ${quantityDecimal}`);
        
        const meetsMinQty = quantityDecimal.greaterThanOrEqualTo(minQty);
        const meetsMaxQty = maxQty ? quantityDecimal.lessThanOrEqualTo(maxQty) : true;
        
        console.log(`[salePriceRuleUtils] Rule ${rule.id} - meetsMinQty: ${meetsMinQty}, meetsMaxQty: ${meetsMaxQty}`);
        
        return meetsMinQty && meetsMaxQty;
      });
      
      console.log(`[salePriceRuleUtils] Found ${quantityMatchedRules.length} rules matching quantity ${quantityDecimal.toString()}`);
      
      if (quantityMatchedRules.length > 0) {
        // Select best profit rule - first site-specific, then general
        let bestRule: ItemSalePriceRule | null = null;

        if (siteId) {
          bestRule = quantityMatchedRules.find(rule => rule.site_id === siteId) || null;
          if (bestRule) {
            console.log(`[salePriceRuleUtils] Selected site-specific profit rule ID ${bestRule.id} matching quantity`);
          }
        }

        // If no site-specific rule found, look for a general rule
        if (!bestRule) {
          bestRule = quantityMatchedRules.find(rule => rule.site_id === null) || null;
          if (bestRule) {
            console.log(`[salePriceRuleUtils] Selected general profit rule ID ${bestRule.id} matching quantity`);
          }
        }

        // Apply the profit rule
        if (bestRule) {
          let calculatedSalePrice = new Decimal(0);
          const ruleValue = tryDecimalParse(bestRule.value);
          console.log(`[salePriceRuleUtils] Rule value: ${ruleValue.toString()}, purchase price: ${purchasePrice.toString()}`);

          if (bestRule.pricing_method === PricingMethodLiteral.PROFIT_PERCENTAGE) {
            const profitMargin = ruleValue.div(100);
            console.log(`[salePriceRuleUtils] Using profit margin: ${profitMargin.toString()} (${ruleValue.toString()}%)`);
            
            calculatedSalePrice = purchasePrice.mul(new Decimal(1).add(profitMargin));
            console.log(`[salePriceRuleUtils] Applied PROFIT_PERCENTAGE rule: ${ruleValue.toString()}% margin added to purchase price ${purchasePrice.toString()}, result: ${calculatedSalePrice.toString()}`);
          } else { // FIXED_PROFIT
            calculatedSalePrice = purchasePrice.add(ruleValue);
            console.log(`[salePriceRuleUtils] Applied FIXED_PROFIT rule: ${ruleValue.toString()} added to purchase price ${purchasePrice.toString()}, result: ${calculatedSalePrice.toString()}`);
          }
          
          // Ensure price is non-negative
          if (calculatedSalePrice.lessThan(0)) {
            console.warn(
              `[salePriceRuleUtils] Calculated sale price for item ${itemId} is negative ` +
              `(${calculatedSalePrice.toFixed(2)}) with rule ${bestRule.id}. Clamping to zero.`
            );
            calculatedSalePrice = new Decimal(0);
          }
          
          // Round based on item's decimal places configuration
          const roundedSalePrice = roundRate(calculatedSalePrice, rateDecimalPlaces);
          
          return {
            salePrice: roundedSalePrice,
            appliedRuleId: bestRule.id,
            source: 'rule',
          };
        }
      }
    }

    // No applicable rules found
    console.log(`[salePriceRuleUtils] No applicable rules found for item ${itemId}`);
    return { salePrice: null, appliedRuleId: null, source: 'item_price' };
  } catch (error) {
    console.error(`[salePriceRuleUtils] Error applying sales item price rules for item ${itemId}:`, error);
    toast.error('Error calculating sale price based on rules.');
    return { salePrice: null, appliedRuleId: null, source: 'item_price' };
  }
}

/**
 * Enhanced function to get final item price details, incorporating sale price rules
 */
export async function getFinalItemPriceDetails(
  itemId: number,
  manualPurchasePrice?: Decimal | string | number | null,
  siteId?: number | null,
  partyId?: number | null,
  quantity: number | string = 1,
  itemTaxRateId?: number | null, // Add parameter to receive tax_rate_id directly
  rateDecimalPlaces: number = 0
): Promise<FinalItemPriceDetails> {
  let rateFromRules: Decimal | null = null;
  let appliedRuleId: number | null = null;
  let ruleSource: 'rule' | 'item_price' | 'purchase_price' | 'not_found' = 'not_found';
  let detailsFromSqlite: SQLitePriceDetails | null = null;

  try {
    console.log(`[salePriceRuleUtils] getFinalItemPriceDetails called for item ${itemId}, siteId: ${siteId || 'none'}, quantity: ${quantity}, taxRateId: ${itemTaxRateId || 'none'}`);
    
    // 1. First, get purchase price if it wasn't manually provided
    let purchasePrice: Decimal | null = null;
    if (manualPurchasePrice !== undefined && manualPurchasePrice !== null) {
      purchasePrice = tryDecimalParse(manualPurchasePrice);
      console.log(`[salePriceRuleUtils] Using manual purchase price: ${purchasePrice.toString()}`);
    } else {
      purchasePrice = await getItemPurchasePrice(itemId, siteId);
      if (purchasePrice) {
        console.log(`[salePriceRuleUtils] Found purchase price in database: ${purchasePrice.toString()}`);
      } else {
        console.log(`[salePriceRuleUtils] No purchase price found for item ${itemId}.`);
      }
    }
    
    // 2. Try to get sale price from rules
    const ruleResult = await calculateSalePriceWithRules(itemId, purchasePrice, siteId, quantity, rateDecimalPlaces);
    rateFromRules = ruleResult.salePrice; // Can be null
    appliedRuleId = ruleResult.appliedRuleId;
    ruleSource = ruleResult.source;

    if (rateFromRules !== null && ruleSource === 'rule') {
      console.log(`[salePriceRuleUtils] Rate from Sales Rule ID ${appliedRuleId}: ${rateFromRules.toFixed(2)} for item ${itemId}`);
    } else {
      console.log(`[salePriceRuleUtils] No applicable sales rule found or rule yielded null price for item ${itemId}. Source: ${ruleSource}. Will use standard pricing.`);
      // Ensure rateFromRules is explicitly null if no rule applied or rule gave null
      rateFromRules = null; 
    }

    // SQLite worker-based access doesn't support window.api
    // All pricing should go through the API backend
    console.log("[salePriceRuleUtils] Using API backend for pricing calculations");

    // 3. Get pricing details from database - with or without rule-based rate
    detailsFromSqlite = await getItemSqliteDetails(
      itemId, 
      rateFromRules, 
      siteId, 
      partyId, 
      quantity,
      itemTaxRateId // Pass the tax_rate_id directly to avoid fetching item again
    );
    
    // Handle case where we got no details back
    if (!detailsFromSqlite) {
      console.warn("[salePriceRuleUtils] SQLite returned null or undefined details, using default values");
      detailsFromSqlite = {
        rate: "0",
        discount_percent: "0",
        discount_amount: "0",
        tax_rate_id: itemTaxRateId || null, // Use passed tax_rate_id if available
        tax_amount: "0",
        tax_breakdown: [],
        final_amount: "0",
        taxable_amount: "0"
      };
    }

    // Log what we're using
    console.log(`[salePriceRuleUtils] Using price details:`, detailsFromSqlite);
    console.log(`[salePriceRuleUtils] Is tax inclusive from SQLite:`, detailsFromSqlite?.is_tax_inclusive);

    // If a rule was applied and forced a rate, the backend's rate might be overridden.
    // Create default values for missing properties (following sqlitePricingUtils.ts pattern)
    const safeDetails = {
      rate: rateFromRules !== null ? rateFromRules.toString() : (detailsFromSqlite.rate ?? "0"),
      discount_percent: detailsFromSqlite.discount_percent ?? "0",
      discount_amount: detailsFromSqlite.discount_amount ?? "0",
      tax_rate_id: detailsFromSqlite.tax_rate_id ?? itemTaxRateId ?? null, // Ensure we use tax_rate_id if available
      tax_amount: detailsFromSqlite.tax_amount ?? "0",
      tax_breakdown: Array.isArray(detailsFromSqlite.tax_breakdown) ? detailsFromSqlite.tax_breakdown : [],
      final_amount: detailsFromSqlite.final_amount ?? "0",
      taxable_amount: detailsFromSqlite.taxable_amount ?? "0",
      applied_sale_rule_id: appliedRuleId, // Pass through the applied rule ID
      source: ruleSource, // Pass through the source information
    };
    
    // Return the final price details
    const parsedRate = tryDecimalParse(safeDetails.rate);
    // Round the rate based on item's decimal places configuration
    const roundedRate = roundRate(parsedRate, rateDecimalPlaces);
    
    const finalResult = {
      rate: roundedRate,
      discount_percent: tryDecimalParse(safeDetails.discount_percent),
      discount_amount: tryDecimalParse(safeDetails.discount_amount),
      tax_rate_id: safeDetails.tax_rate_id,
      tax_amount: tryDecimalParse(safeDetails.tax_amount),
      tax_breakdown: safeDetails.tax_breakdown.map((b: { code?: string, rate: string | number, amount: string | number }) => ({
        code: b.code || "TAX",
        rate: tryDecimalParse(b.rate),
        amount: tryDecimalParse(b.amount),
      })),
      final_amount: tryDecimalParse(safeDetails.final_amount),
      taxable_amount: tryDecimalParse(safeDetails.taxable_amount),
      applied_sale_rule_id: safeDetails.applied_sale_rule_id,
      source: safeDetails.source,
      is_tax_inclusive: detailsFromSqlite?.is_tax_inclusive ?? false
    };
    
    console.log(`[salePriceRuleUtils] Final result with is_tax_inclusive:`, {
      itemId,
      is_tax_inclusive: finalResult.is_tax_inclusive,
      rate: finalResult.rate.toString(),
      tax_amount: finalResult.tax_amount.toString(),
      final_amount: finalResult.final_amount.toString()
    });
    
    return finalResult;
  } catch (error) {
    console.error(`[salePriceRuleUtils] Error in getFinalItemPriceDetails for item ${itemId}:`, error);
    toast.error(`Error calculating final price: ${error instanceof Error ? error.message : 'Unknown error'}`);
    return getDefaultPriceDetails();
  }
}

/**
 * Helper function for getting pricing details from local SQLite
 */
async function getItemSqliteDetails(
  itemId: number,
  forcedRate: Decimal | null,
  siteId?: number | null,
  partyId?: number | null,
  quantity: number | string = 1,
  itemTaxRateId?: number | null // Add parameter to receive tax_rate_id directly
): Promise<SQLitePriceDetails> {
  const forcedRateStr = forcedRate !== null ? forcedRate.toString() : undefined;
  
  try {
    console.log(`[salePriceRuleUtils] Getting price details for item ${itemId}, rate: ${forcedRateStr || 'fetching from local SQLite'}`);
    
    // Import the async database functions
    const { getItemPriceAsync, getItemPriceDetailsAsync } = await import('@/lib/indexeddb/database');
    
    // If we have a forced rate (from a rule), use it
    if (forcedRateStr) {
      // Get is_tax_inclusive flag for the item
      const priceDetails = await getItemPriceDetailsAsync(itemId, 'sale', siteId);
      const isTaxInclusive = priceDetails?.is_tax_inclusive || false;
      
      console.log(`[salePriceRuleUtils] Item ${itemId} tax-inclusive flag:`, {
        priceDetails,
        isTaxInclusive,
        forcedRateStr
      });
      
      // Create a result with the forced rate
      const result: SQLitePriceDetails = {
        rate: forcedRateStr,
        discount_percent: "0",
        discount_amount: "0",
        tax_rate_id: itemTaxRateId || null,
        tax_amount: "0",
        tax_breakdown: [],
        final_amount: forcedRateStr,
        taxable_amount: forcedRateStr,
        is_tax_inclusive: isTaxInclusive
      };
      
      // Calculate tax if we have tax_rate_id
      if (result.tax_rate_id) {
        try {
          const { getTaxRateAsync } = await import('@/lib/indexeddb/database');
          const taxRate = await getTaxRateAsync(result.tax_rate_id);
          
          if (taxRate && taxRate.rate) {
            const qty = typeof quantity === 'number' ? quantity : parseFloat(String(quantity) || '1');
            const rate = parseFloat(forcedRateStr);
            const grossAmount = rate * qty;
            const taxPercent = parseFloat(String(taxRate.rate));
            let taxAmount: number;
            let taxableAmount: number;
            
            if (isTaxInclusive) {
              // For tax-inclusive pricing, extract tax from the gross amount
              // Formula: tax = (amount * tax_rate) / (100 + tax_rate)
              taxAmount = (grossAmount * taxPercent) / (100 + taxPercent);
              taxableAmount = grossAmount - taxAmount;
              
              console.log(`[salePriceRuleUtils] Tax-inclusive calculation: gross=${grossAmount}, tax=${taxPercent}%, taxAmount=${taxAmount}, taxable=${taxableAmount}`);
              
              result.taxable_amount = taxableAmount.toString();
              result.tax_amount = taxAmount.toString();
              result.final_amount = grossAmount.toString(); // Final amount is the inclusive price
            } else {
              // For tax-exclusive pricing, add tax on top
              taxAmount = (grossAmount * taxPercent) / 100;
              taxableAmount = grossAmount;
              
              console.log(`[salePriceRuleUtils] Tax-exclusive calculation: gross=${grossAmount}, tax=${taxPercent}%, taxAmount=${taxAmount}`);
              
              result.taxable_amount = taxableAmount.toString();
              result.tax_amount = taxAmount.toString();
              result.final_amount = (grossAmount + taxAmount).toString();
            }
            
            result.tax_breakdown = [{
              code: taxRate.name || 'TAX',
              rate: taxPercent,
              amount: taxAmount
            }];
          }
        } catch (error) {
          console.error(`[salePriceRuleUtils] Error calculating tax:`, error);
        }
      }
      
      return result;
    }
    
    // No forced rate, so fetch the sale price details from local SQLite
    const priceDetails = await getItemPriceDetailsAsync(itemId, 'sale', siteId);
    
    console.log(`[salePriceRuleUtils] Fetched price details for item ${itemId}:`, priceDetails);
    
    if (priceDetails && priceDetails.price !== null) {
      const salePrice = priceDetails.price;
      const isTaxInclusive = priceDetails.is_tax_inclusive;
      
      console.log(`[salePriceRuleUtils] Found sale price in local SQLite: ${salePrice}, tax-inclusive: ${isTaxInclusive}`);
      
      const result: SQLitePriceDetails = {
        rate: salePrice.toString(),
        discount_percent: "0",
        discount_amount: "0",
        tax_rate_id: itemTaxRateId || null,
        tax_amount: "0",
        tax_breakdown: [],
        final_amount: salePrice.toString(),
        taxable_amount: salePrice.toString(),
        is_tax_inclusive: isTaxInclusive
      };
      
      // Calculate tax if we have tax_rate_id
      if (result.tax_rate_id) {
        try {
          const { getTaxRateAsync } = await import('@/lib/indexeddb/database');
          const taxRate = await getTaxRateAsync(result.tax_rate_id);
          
          if (taxRate && taxRate.rate) {
            const qty = typeof quantity === 'number' ? quantity : parseFloat(String(quantity) || '1');
            const rate = parseFloat(String(salePrice));
            const grossAmount = rate * qty;
            const taxPercent = parseFloat(String(taxRate.rate));
            let taxAmount: number;
            let taxableAmount: number;
            
            if (isTaxInclusive) {
              // For tax-inclusive pricing, extract tax from the gross amount
              taxAmount = (grossAmount * taxPercent) / (100 + taxPercent);
              taxableAmount = grossAmount - taxAmount;
              
              console.log(`[salePriceRuleUtils] Tax-inclusive calculation: gross=${grossAmount}, tax=${taxPercent}%, taxAmount=${taxAmount}, taxable=${taxableAmount}`);
              
              result.taxable_amount = taxableAmount.toString();
              result.tax_amount = taxAmount.toString();
              result.final_amount = grossAmount.toString(); // Final amount is the inclusive price
            } else {
              // For tax-exclusive pricing, add tax on top
              taxAmount = (grossAmount * taxPercent) / 100;
              taxableAmount = grossAmount;
              
              console.log(`[salePriceRuleUtils] Tax-exclusive calculation: gross=${grossAmount}, tax=${taxPercent}%, taxAmount=${taxAmount}`);
              
              result.taxable_amount = taxableAmount.toString();
              result.tax_amount = taxAmount.toString();
              result.final_amount = (grossAmount + taxAmount).toString();
            }
            
            result.tax_breakdown = [{
              code: taxRate.name || 'TAX',
              rate: taxPercent,
              amount: taxAmount
            }];
          }
        } catch (error) {
          console.error(`[salePriceRuleUtils] Error calculating tax:`, error);
        }
      }
      
      return result;
    }
    
    console.warn(`[salePriceRuleUtils] No sale price found in local SQLite for item ${itemId}`);
    return createDefaultSQLiteResult("0");
  } catch (error) {
    console.error(`[salePriceRuleUtils] Error in getItemSqliteDetails:`, error);
    return createDefaultSQLiteResult(forcedRateStr);
  }
}

// Helper function to create a default SQLite result object
function createDefaultSQLiteResult(rate: string = "0"): SQLitePriceDetails {
  console.log(`[TAX DEBUG] Creating default SQLite result with rate: ${rate}`);
  
  // Ensure rate is not empty/undefined
  const safeRate = rate && rate !== '' ? rate : "0";
  
  return {
    rate: safeRate,
    discount_percent: "0",
    discount_amount: "0",
    tax_rate_id: null,
    tax_amount: "0",
    tax_breakdown: [],
    final_amount: safeRate, // Basic approximation
    taxable_amount: safeRate, // Basic approximation
    is_tax_inclusive: false, // Default to tax-exclusive
  };
}

// Update getDefaultPriceDetails to include source information
function getDefaultPriceDetails(appliedRuleId: number | null = null, source: 'rule' | 'item_price' | 'purchase_price' | 'not_found' = 'not_found'): FinalItemPriceDetails {
  return {
    rate: new Decimal(0),
    discount_percent: new Decimal(0),
    discount_amount: new Decimal(0),
    tax_rate_id: null,
    tax_amount: new Decimal(0),
    tax_breakdown: [],
    final_amount: new Decimal(0),
    taxable_amount: new Decimal(0),
    applied_sale_rule_id: appliedRuleId,
    source: source,
    is_tax_inclusive: false
  };
}

/**
 * Simplified function for recalculating price details in the EditableDataGrid
 * This function can be used when quantity, rate, or discount changes
 * This is a synchronous version that calculates without tax (tax rate must be passed separately)
 * For complete tax calculations with database lookup, use recalculateItemPriceAsync
 */
export function recalculateItemPrice(
  itemId: number | undefined | null,
  taxRateId: number | undefined | null, 
  quantity: number | string,
  rate: number | string,
  discountPercent: number | string,
  discountAmount?: number | string,
  isTaxInclusive: boolean = false,
  taxRatePercent?: number, // Optional tax rate percentage to avoid async lookup
  rateDecimalPlaces: number = 0
): { 
  taxableAmount: number; 
  taxAmount: number; 
  totalAmount: number;
} {
  if (!itemId) {
    return { taxableAmount: 0, taxAmount: 0, totalAmount: 0 };
  }

  // Parse numeric values
  const qty = tryDecimalParse(quantity || 0);
  const parsedRate = tryDecimalParse(rate || 0);
  // Round the rate based on item's decimal places configuration
  const unitRate = roundRate(parsedRate, rateDecimalPlaces);
  const discPct = tryDecimalParse(discountPercent || 0);
  const discAmt = tryDecimalParse(discountAmount || 0);

  // Calculate gross amount
  const grossAmount = qty.mul(unitRate);
  
  // Calculate discount - use discount amount if provided, otherwise calculate from percentage
  let calculatedDiscountAmount: Decimal;
  if (discAmt.gt(0)) {
    calculatedDiscountAmount = discAmt;
  } else if (discPct.gt(0)) {
    calculatedDiscountAmount = grossAmount.mul(discPct).div(100);
  } else {
    calculatedDiscountAmount = new Decimal(0);
  }
  
  let taxableAmount = grossAmount.sub(calculatedDiscountAmount);
  let totalAmount = taxableAmount;
  let taxAmount = new Decimal(0);

  // If not taxable or no tax rate, return without tax
  if (!taxRateId || !taxRatePercent) {
    return {
      taxableAmount: taxableAmount.toNumber(),
      taxAmount: 0,
      totalAmount: taxableAmount.toNumber()
    };
  }

  // Calculate tax based on the provided rate percentage
  const totalTaxRate = new Decimal(taxRatePercent);
  
  if (isTaxInclusive) {
    // For tax-inclusive pricing, extract tax from the amount
    // Formula: tax = (amount * tax_rate) / (100 + tax_rate)
    taxAmount = taxableAmount.mul(totalTaxRate).div(new Decimal(100).add(totalTaxRate));
    // Adjust taxable amount to exclude tax
    taxableAmount = taxableAmount.sub(taxAmount);
    // Total amount remains the same (inclusive price)
    totalAmount = taxableAmount.add(taxAmount);
  } else {
    // For tax-exclusive pricing, add tax on top
    taxAmount = taxableAmount.mul(totalTaxRate).div(100);
    totalAmount = taxableAmount.add(taxAmount);
  }
  
  return {
    taxableAmount: taxableAmount.toDecimalPlaces(2).toNumber(),
    taxAmount: taxAmount.toDecimalPlaces(2).toNumber(),
    totalAmount: totalAmount.toDecimalPlaces(2).toNumber()
  };
}

/**
 * Async version of recalculateItemPrice that fetches tax rate from IndexedDB
 * Use this when you need accurate tax calculations with database lookup
 */
export async function recalculateItemPriceAsync(
  itemId: number | undefined | null,
  taxRateId: number | undefined | null, 
  quantity: number | string,
  rate: number | string,
  discountPercent: number | string,
  discountAmount?: number | string,
  isTaxInclusive: boolean = false,
  rateDecimalPlaces: number = 0
): Promise<{ 
  taxableAmount: number; 
  taxAmount: number; 
  totalAmount: number;
}> {
  if (!itemId || !taxRateId) {
    return recalculateItemPrice(itemId, taxRateId, quantity, rate, discountPercent, discountAmount, isTaxInclusive, undefined, rateDecimalPlaces);
  }

  try {
    // Import the async database function
    const { getTaxRateAsync } = await import('@/lib/indexeddb/database');
    
    // Find tax rate from IndexedDB
    const taxRate = await getTaxRateAsync(taxRateId);
    if (!taxRate || !taxRate.rate) {
      console.warn(`[recalculateItemPriceAsync] Tax rate ${taxRateId} not found in IndexedDB`);
      return recalculateItemPrice(itemId, taxRateId, quantity, rate, discountPercent, discountAmount, isTaxInclusive, undefined, rateDecimalPlaces);
    }

    // Use the synchronous version with the fetched tax rate
    return recalculateItemPrice(
      itemId, 
      taxRateId, 
      quantity, 
      rate, 
      discountPercent, 
      discountAmount, 
      isTaxInclusive,
      parseFloat(String(taxRate.rate)),
      rateDecimalPlaces
    );
  } catch (error) {
    console.error(`[recalculateItemPriceAsync] Error fetching tax rate from IndexedDB:`, error);
    // Return values without tax if there's an error
    return recalculateItemPrice(itemId, taxRateId, quantity, rate, discountPercent, discountAmount, isTaxInclusive, undefined, rateDecimalPlaces);
  }
}